import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useWatch } from 'react-hook-form';
import type { FormEachNode } from '../../types/layout';
import type { FormFieldMapping } from '../../types/fieldMapping';
import { FormFieldAdapter } from './FormFieldAdapter';
import { FormRowProvider } from '../../context/FormRowContext';
import { useFormEngineForm, useFormEngineState } from '../../context/FormEngineContext';
import { useEffectProcessor } from '../../processors/effectProcessor';
import { useObserverProcessor } from '../../processors/observerProcessor';
import { useRowObserverProcessor } from '../../processors/rowObserverProcessor';
import { prefixEffects, prefixObservers } from '../../processors/pathRewriter';
import { resolveItemTokens } from '../../resolver/tokenResolver';
import { useFormEachArray } from '../../hooks/useFormEachArray';
import { FormEachAddButton } from './each/FormEachAddButton';
import { FormEachRemoveButton } from './each/FormEachRemoveButton';
import { MatrixFieldsRoot } from './MatrixFieldsMesh';
import { useMatrixColumnCount } from '../../context/MatrixColumnCountContext';
import { applyTitleFrom } from './each/applyTitleFrom';

/**
 * Tag-row presentation — the "field, field, [+]" horizontal layout.
 *
 * All items live in ONE shared MatrixFieldsRoot grid.
 * Each item = 1 column cell containing [field + inline remove].
 * The add button = the last cell in the same row.
 * Items wrap naturally when they exceed the available columns.
 *
 * Use for single-field primitive arrays: domain names, tags, email lists.
 * Compare with `inline` (which gives each item its own full-width row).
 *
 * ── Alignment contract ──────────────────────────────────────────────────
 * FormField renders: [label row] → [input control] → [error text]
 * The delete button must align to the INPUT CONTROL, not the field root
 * bottom (which shifts when an error message appears below the input).
 *
 * Solution: a CSS-Grid 3-row layout on TagItemCell.
 * Row 1 (label):    FormField occupies it; button cell is empty
 * Row 2 (control):  FormField's input + button aligned to center
 * Row 3 (message):  FormField's error/hint text; button cell is empty
 *
 * We achieve this WITHOUT splitting FormField by wrapping the field in
 * a 3-row grid and absolutely positioning the button in row 2 via a
 * separate grid area that spans only the control row height.
 *
 * In practice the simplest CSS that matches legacy behaviour:
 *   - Label height ≈ 26px (22px line-height + 4px margin)
 *   - Input height ≈ 40px
 *   - The button container uses padding-top equal to label height so the
 *     button sits next to the input, not at the bottom of the whole field.
 *   - When there's an error the button stays at input level because we
 *     are NOT using align-items:flex-end on the outer row.
 * ────────────────────────────────────────────────────────────────────────
 */

/**
 * Outer cell in the shared MatrixFieldsRoot grid.
 * Uses a 3-row sub-grid: label / input / message
 * This lets the delete button align to the input row regardless of whether
 * an error message is visible below.
 */
const TagItemCell = styled.div`
  box-sizing: border-box;
  min-width: 0;
  /* 3-row layout: [label ~26px] [input ~40px] [message auto] */
  display: grid;
  /* label row: label height (22px line-height + 4px margin-bottom = 26px) */
  grid-template-rows: 26px 40px auto;
  grid-template-columns: 1fr auto;
  column-gap: 4px;
`;

/**
 * The FormField sits in columns 1, spanning all 3 rows.
 * We override its internal display so the label, input, and error each
 * naturally occupy their row via normal document flow.
 */
const TagItemFieldWrapper = styled.div`
  grid-column: 1;
  grid-row: 1 / -1;
  min-width: 0;
`;

/**
 * Empty spacer in column 2, row 1 — keeps the grid structure intact
 * while leaving the label row unoccupied in the button column.
 */
const TagItemLabelSpacer = styled.div`
  grid-column: 2;
  grid-row: 1;
`;

/**
 * The delete button sits in column 2, row 2 (the input row).
 * It is always aligned to the input regardless of label height or
 * whether an error message is showing below.
 */
const TagItemRemoveCell = styled.div`
  grid-column: 2;
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/**
 * The add button cell — aligns to the input row by using the same
 * 3-row structure: top padding = label height, cell height = input height.
 */
const TagAddCell = styled.div`
  box-sizing: border-box;
  min-width: 0;
  display: grid;
  grid-template-rows: 26px 40px auto;
  grid-template-columns: 1fr;
`;

const TagAddButtonRow = styled.div`
  grid-row: 2;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

interface FormEachTagRowRendererProps {
  node: FormEachNode;
}

/** View-mode cell — no form label/input row mesh; ViewField owns its layout. */
const TagItemViewCell = styled.div`
  box-sizing: border-box;
  min-width: 0;
`;

export const FormEachTagRowRenderer = React.memo(function FormEachTagRowRenderer({
  node,
}: FormEachTagRowRendererProps): React.ReactElement {
  const {
    fields,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
  } = useFormEachArray(node);
  const { isReadOnly } = useFormEngineState();

  const matrixColumns = useMatrixColumnCount();
  const totalColumns =
    typeof node.columns === 'number' ? node.columns : matrixColumns;

  const showRemovePerRow = canRemoveRow();

  return (
    <MatrixFieldsRoot columnCount={totalColumns}>
      {fields.map((item, index) => (
        <TagRowItem
          key={item.id}
          node={node}
          index={index}
          total={fields.length}
          showRemove={showRemovePerRow && !isReadOnly}
          onRemove={handleRemove}
          viewMode={isReadOnly}
        />
      ))}

      {showAdd && !isReadOnly && (
        <TagAddCell>
          <TagAddButtonRow>
            <FormEachAddButton node={node} onClick={handleAdd} />
          </TagAddButtonRow>
        </TagAddCell>
      )}
    </MatrixFieldsRoot>
  );
});

interface TagRowItemProps {
  node: FormEachNode;
  index: number;
  total: number;
  showRemove: boolean;
  onRemove: (index: number) => void;
  /** Read-only / view — skip form input row mesh. */
  viewMode?: boolean;
}

const TagRowItem = React.memo(function TagRowItem({
  node,
  index,
  total,
  showRemove,
  onRemove,
  viewMode = false,
}: TagRowItemProps): React.ReactElement {
  const { form, programmaticWriteGuard } = useFormEngineForm();
  const { meta, getOptionProp } = useFormEngineState();
  const namePrefix = `${node.name}.${index}`;
  const rowValues = useWatch({ control: form.control, name: namePrefix }) as
    | Record<string, unknown>
    | undefined;

  const rowMeta = useMemo(
    () => ({
      index,
      total,
      isFirst: index === 0,
      isLast: index === total - 1,
      documentCategory: node.documentCategory,
    }),
    [index, total, node.documentCategory],
  );

  const resolvedItemFields = useMemo(
    () =>
      node.itemFields.map((field) => {
        const resolved = resolveItemTokens(field, namePrefix, index) as FormFieldMapping;
        const withTitle = applyTitleFrom(resolved, rowValues);
        // def is preserved by reference in resolveItemTokens (to avoid schema cloning).
        // Apply {n} substitution separately via overrideLabel so the label renders
        // as "Domain Name 1", "Domain Name 2", etc.
        const rawLabel = field.def.label ?? '';
        if (rawLabel.includes('{n}')) {
          return { ...withTitle, overrideLabel: rawLabel.replace(/\{n\}/g, String(index + 1)) };
        }
        return withTitle;
      }),
    [node.itemFields, namePrefix, index, rowValues],
  );

  const scopedEffects = useMemo(
    () => (node.itemEffects?.length ? prefixEffects(node.itemEffects, namePrefix) : []),
    [node.itemEffects, namePrefix],
  );

  const scopedObservers = useMemo(
    () => (node.itemObservers?.length ? prefixObservers(node.itemObservers, namePrefix) : []),
    [node.itemObservers, namePrefix],
  );

  useEffectProcessor({ form: form as never, effects: scopedEffects, meta, getOptionProp, programmaticWriteGuard });
  useObserverProcessor({ form: form as never, observers: scopedObservers, meta, getOptionProp });
  useRowObserverProcessor({
    form: form as never,
    observers: node.itemRowObservers ?? [],
    rowPrefix: namePrefix,
    meta,
    getOptionProp,
  });

  if (viewMode) {
    return (
      <FormRowProvider value={rowMeta}>
        <TagItemViewCell>
          {resolvedItemFields.map((field, fieldIndex) => (
            <FormFieldAdapter
              key={field.name || `__tag_field_${fieldIndex}`}
              field={field}
              namePrefix={namePrefix}
            />
          ))}
        </TagItemViewCell>
      </FormRowProvider>
    );
  }

  return (
    <FormRowProvider value={rowMeta}>
      {/*
       * 3-row sub-grid: [label ~26px] / [input ~40px] / [error auto]
       * Column 1: FormField spans all 3 rows (full document flow)
       * Column 2 row 1: empty spacer (label row)
       * Column 2 row 2: delete button (input row — always aligned to the input)
       * Column 2 row 3: implicit (no content)
       */}
      <TagItemCell>
        {resolvedItemFields.map((field, fieldIndex) => (
          <React.Fragment key={field.name || `__tag_field_${fieldIndex}`}>
            <TagItemFieldWrapper>
              <FormFieldAdapter field={field} namePrefix={namePrefix} />
            </TagItemFieldWrapper>
            {showRemove && fieldIndex === resolvedItemFields.length - 1 && (
              <>
                <TagItemLabelSpacer aria-hidden='true' />
                <TagItemRemoveCell>
                  <FormEachRemoveButton node={node} index={index} onRemove={onRemove} />
                </TagItemRemoveCell>
              </>
            )}
          </React.Fragment>
        ))}
      </TagItemCell>
    </FormRowProvider>
  );
});
